import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { AutoSizer } from 'react-virtualized/dist/es/AutoSizer';
import { List } from 'react-virtualized/dist/es/List';
import * as cx from 'classnames';
import { searchArea } from 'actions/regions/areas';
import IconButton from 'material-ui/IconButton/IconButton';
import SearchIcon from 'material-ui/svg-icons/action/search';
import CircularProgress from 'material-ui/CircularProgress';
import LightbulbOutlineIcon from 'material-ui/svg-icons/action/lightbulb-outline';
import { matchGeoNames, clearGeoNames } from 'services/regions/areas';
import GeoMatchPopover from './GeoMatchPopover';
import Search from './Search';
import Filter from './Filter';


function recognizedScoresFromArea(area, manuallyMatchedGeo = {}, clearedGeoMatched = {}) {
    if (clearedGeoMatched[area._id]) {
        return { portScore: null, regionScore: null };
    }
    const recognized = (area.namesMatchGeo && area.namesMatchGeo.recognized) || manuallyMatchedGeo[area._id];
    const portScore = recognized && recognized.port && typeof recognized.port.score === 'number'
        ? Math.round(recognized.port.score * 100)
        : null;
    const regionScore = recognized && recognized.region && typeof recognized.region.score === 'number'
        ? Math.round(recognized.region.score * 100)
        : null;
    return { portScore, regionScore };
}

const pastelColors = {
    green: { border: '#81C784', color: '#2E7D32', bg: '#E8F5E9' },
    yellow: { border: '#FFD54F', color: '#F57F17', bg: '#FFF8E1' },
    red: { border: '#E57373', color: '#C62828', bg: '#FFEBEE' },
};

function getScoreColors(score) {
    if (score == null) return null;
    if (score >= 80) return pastelColors.green;
    if (score >= 50) return pastelColors.yellow;
    return pastelColors.red;
}


const ROW_HEIGHT = 48;

const FilterAreas = (props) => {
    const [areaName, setAreaName] = useState('');
    const [filter, setFilter] = useState('all');
    const [filteredAreas, setFilteredAreas] = useState([]);
    const [popoverAnchor, setPopoverAnchor] = useState(null);
    const [popoverArea, setPopoverArea] = useState(null);
    const [manuallyMatchedGeo, setManuallyMatchedGeo] = useState({});
    const [matchingLoading, setMatchingLoading] = useState({});
    const [clearedGeoMatched, setClearedGeoMatched] = useState({});
    const listRef = useRef(null);

    const handlePopoverOpen = (e, area) => {
        setPopoverAnchor(e.currentTarget);
        setPopoverArea(area);
    };

    const handlePopoverClose = () => {
        setPopoverAnchor(null);
        setPopoverArea(null);
    };

    const handleManualMatch = (area) => {
        setMatchingLoading(prev => ({ ...prev, [area._id]: true }));
        matchGeoNames([{ name: area.name, _id: area._id }])
            .then((response) => {
                const responseData = response && response.data ? response.data : response;
                if (responseData && responseData.success && responseData.results && responseData.results.length > 0) {
                    const matchedRow = responseData.results[0];
                    if (matchedRow && matchedRow.recognized) {
                        setClearedGeoMatched(prev => {
                            const copy = { ...prev };
                            delete copy[area._id];
                            return copy;
                        });
                        setManuallyMatchedGeo(prev => ({
                            ...prev,
                            [area._id]: matchedRow.recognized
                        }));
                    }
                }
                setMatchingLoading(prev => ({ ...prev, [area._id]: false }));
            })
            .catch((err) => {
                console.error('Error matching geo names:', err);
                setMatchingLoading(prev => ({ ...prev, [area._id]: false }));
            });
    };

    const handleClear = (area) => {
        setMatchingLoading(prev => ({ ...prev, [area._id]: true }));
        clearGeoNames([area._id])
            .then((response) => {
                const responseData = response && response.data ? response.data : response;
                if (responseData && typeof responseData.modifiedCount === 'number') {
                    setClearedGeoMatched(prev => ({
                        ...prev,
                        [area._id]: true
                    }));
                    setManuallyMatchedGeo(prev => {
                        const copy = { ...prev };
                        delete copy[area._id];
                        return copy;
                    });
                }
                setMatchingLoading(prev => ({ ...prev, [area._id]: false }));
                handlePopoverClose();
            })
            .catch((err) => {
                console.error('Error clearing geo names:', err);
                setMatchingLoading(prev => ({ ...prev, [area._id]: false }));
                handlePopoverClose();
            });
    };


    const checkSelect = (item) => {
        const updatedAreas = filteredAreas.map(area => ({
            ...area,
            isActive: item._id === area._id,
        }));

        setFilteredAreas(updatedAreas);
        props.onSelectArea(_.cloneDeep(item));
    };

    const filterAreas = (areas) => {
        const filtered = areas.filter((area) => {
            if (area.name.toLowerCase().includes(areaName.toLowerCase())) {
                return true;
            }

            if (!area.fields) {
                return false;
            }

            return area.fields.some(field =>
                field.name.toLowerCase().includes(areaName.toLowerCase()));
        });

        switch (filter) {
            case 'noPorts':
                return filtered
                    .filter(a => !a.mainRegion)
                    .filter(area => area.childs.length === 0);
            case 'noMainPorts':
                return filtered
                    .filter(a => !a.mainRegion)
                    .filter(area => !(area.mainPorts && area.mainPorts.length > 0));
            case 'mainRegions':
                return filtered.filter(area => area.mainRegion);
            default:
                return filtered.filter(a => !a.mainRegion);
        }
    };

    const changeSearch = (value) => {
        setAreaName(value);
    };

    const changeFilter = (value) => {
        setFilter(value);
    };

    useEffect(() => {
        setFilteredAreas(filterAreas(props.areas.items));
    }, [filter, areaName]);

    useEffect(() => {
        setFilteredAreas(filterAreas(props.areas.items));
    }, [props.areas.items]);

    useEffect(() => {
        if (props.selectedArea) {
            const areaIndex = filteredAreas.findIndex(area => area._id === props.selectedArea);
            if (listRef.current) {
                listRef.current.scrollToRow(areaIndex);
            }
        }
    }, [props.selectedArea, filteredAreas]);

    useEffect(() => {
        if (listRef.current && listRef.current.recomputeRowHeights) {
            listRef.current.recomputeRowHeights();
        }
    }, [filteredAreas]);

    const renderRow = ({ index, key, style }) => {
        const area = filteredAreas[index];
        const selected = props.selectedArea === area._id;
        const parentCount = (area.parents || [])
            .map(id => props.areas.items.find(a => a._id === id))
            .reduce((count, parent) => (parent ? count + 1 : count), 0);

        const { portScore, regionScore } = recognizedScoresFromArea(area, manuallyMatchedGeo, clearedGeoMatched);
        const showGeoMatchScores = portScore != null || regionScore != null;
        const portStyle = getScoreColors(portScore);
        const regionStyle = getScoreColors(regionScore);

        return (
            <div
                role='button'
                tabIndex={0}
                style={{
                    ...style,
                    display: 'flex',
                    // alignItems: 'center',
                    justifyContent: 'space-between',
                    fontWeight: area.mainRegion ? 'bold' : 'normal',
                }}
                key={key}
                className={cx('list-item', { active: selected, 'list-item--geo-match': showGeoMatchScores })}
                onClick={() => { checkSelect(area); }}
                onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                        checkSelect(area);
                    }
                }}
            >
                <div className='list-item__primary'>
                    <div className='list-item__primary-title'>
                        {area.name}
                    </div>
                    <div className='list-item__primary-meta'>
                        <b style={{ color: parentCount > 1 ? 'rgb(255, 64, 129)' : '' }}>
                            {parentCount}
                        </b>
                        {parentCount === 1 ? ' parent ' : ' parents '}
                        <b>{area.childAreaCount}</b>
                        {area.childAreaCount === 1 ? ' child ' : ' children '}
                        <b>{area.childPortCount}</b>
                        {area.childPortCount === 1 ? ' port' : ' ports'}
                    </div>
                </div>
                <div className='list-item__trail'>
                    {showGeoMatchScores ? (
                        <button
                            type='button'
                            className='geo-match-btn-group'
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePopoverOpen(e, area);
                            }}
                            style={{
                                marginRight: 2,
                                border: '1px solid rgba(0, 188, 212, 0.24)',
                                color: '#00838f',
                                backgroundColor: 'rgba(0, 188, 212, 0.05)',
                            }}
                        >
                            {portScore != null && (
                                <div className='geo-match-btn-line'>
                                    P: <span style={portStyle ? { color: portStyle.color } : {}}>{portScore}%</span>
                                </div>
                            )}
                            {regionScore != null && (
                                <div className='geo-match-btn-line'>
                                    R: <span style={regionStyle ? { color: regionStyle.color } : {}}>{regionScore}%</span>
                                </div>
                            )}
                        </button>
                    ) : (
                        matchingLoading[area._id] ? (
                            <CircularProgress
                                size={18}
                                thickness={2}
                                style={{
                                    width: 32,
                                    height: 32,
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    alignSelf: 'center',
                                    marginRight: 2,
                                }}
                            />
                        ) : (
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleManualMatch(area);
                                }}
                                style={{
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    alignSelf: 'center',
                                    marginRight: 2,
                                }}
                                iconStyle={{
                                    width: 18,
                                    height: 18,
                                    margin: 0,
                                    padding: 0,
                                }}
                            >
                                <LightbulbOutlineIcon />
                            </IconButton>
                        )
                    )}
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            props.searchArea(area.name);
                            props.router.push('/regions/altareas');
                        }}
                        style={{
                            width: 32,
                            height: 32,
                            padding: 0,
                            display: 'inline-flex',
                            justifyContent: 'center',
                            alignSelf: 'center',
                        }}
                        iconStyle={{
                            width: 18,
                            height: 18,
                            margin: 0,
                            padding: 0,
                        }}
                    >
                        <SearchIcon />
                    </IconButton>
                </div>
            </div>
        );
    };

    return (
        <div className='area-panel'>
            <Search changeSearch={changeSearch} />
            <Filter changeFilter={changeFilter} />

            <div className='paper-block margin-10 area-list'>
                <span className='name-block absolute small-name'>Areas to select</span>

                <div className='list'>
                    <AutoSizer>
                        {
                            ({ width, height }) => (<List
                                ref={listRef}
                                _updateOnly={props.areas}
                                selectedArea={props.selectedArea} // only to cause update
                                rowRenderer={renderRow}
                                height={height}
                                width={width}
                                rowHeight={ROW_HEIGHT}
                                rowCount={filteredAreas.length}
                            />)
                        }
                    </AutoSizer>
                </div>
            </div>

            <GeoMatchPopover
                open={!!popoverAnchor && !!popoverArea}
                anchorEl={popoverAnchor}
                onClose={handlePopoverClose}
                area={popoverArea ? {
                    ...popoverArea,
                    namesMatchGeo: clearedGeoMatched[popoverArea._id] ? null : {
                        ...(popoverArea.namesMatchGeo || {}),
                        recognized: (popoverArea.namesMatchGeo && popoverArea.namesMatchGeo.recognized) || manuallyMatchedGeo[popoverArea._id]
                    }
                } : null}
                router={props.router}
                onClear={handleClear}
                onStage={(isPort, target) => {
                    if (target && target.refReal === popoverArea._id) {
                        return; // prevent staging if target is identical to source
                    }
                    checkSelect(popoverArea);
                    if (props.router) {
                        props.router.push({
                            pathname: '/regions/dashboard',
                            state: {
                                sourceArea: popoverArea,
                                destination: {
                                    _id: target.refReal,
                                    name: target.realName,
                                },
                                isPort,
                                stagedAt: Date.now(),
                            },
                        });
                    }
                }}
            />
        </div>
    );
};

export default connect(
    (state, {
        selectedArea, areas, onSelectArea, router,
    }) => ({
        selectedArea,
        areas,
        onSelectArea,
        router,
    }),
    dispatch => bindActionCreators(
        {
            searchArea,
        },
        dispatch,
    ),
)(FilterAreas);



// WEBPACK FOOTER //
// ./app/components/Regions/Dashboard/FilterAreas/Component.jsx